import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { url } from "../../Constant/TextConstant";
import "./ServiceSubCategoryScreen.css";
import { Helmet } from "react-helmet";
import { useUserLocation } from "../../utils/useUserLocation";
import Header from "../Header";
import { apiFetch } from "../../utils/apiClient.js";
const ServiceSubCategoryScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { category, image } = location.state || {};

  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // Replace the existing location state with the useUserLocation hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

   useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handler for modal close with location update
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      // Update location through the hook's update function
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };

  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        setLoading(true);
        const response = await apiFetch(
          `${url.baseAPIUrl}/api/ServicesSubCategory/GetSubCategories?category=${category}`
        );

        if (!response.ok) {
          throw new Error("Failed to load subcategories");
        }

        const data = await response.json();
        setSubCategories(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (category) {
      fetchSubCategories();
    }
  }, [category]);

  const filteredSubCategories = subCategories.filter((subCategory) =>
    subCategory.SubCategory.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubCategoryClick = (subCategory) => {
    navigate(`/browseService/${encodeURIComponent(subCategory.SubCategory)}`, {
      state: { subCategoryDetails: subCategory },
    });
  };

  return (
    <>
      {" "}
      <Helmet>
        <title>
          {category
            ? `${category} Subcategories | Services`
            : "Service Subcategories"}
        </title>
        <meta
          name="description"
          content={
            category
              ? `Browse ${
                  subCategories.length
                } ${category.toLowerCase()} subcategories and find the perfect service`
              : "Explore our service subcategories"
          }
        />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={
            category
              ? `${category} Subcategories | Services`
              : "Service Subcategories"
          }
        />
        <meta
          property="og:description"
          content={
            category
              ? `Find and book ${category.toLowerCase()} services from various subcategories`
              : "Discover and book services across different categories"
          }
        />
        <meta
          property="og:image"
          content={
            image
              ? `${url.serviceImageStartingPath}/${image}`
              : "/images/default-service-category.jpg"
          }
        />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Header
        userLocation={currentLocation} // Use currentLocation from hook
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use updateLocation from hook
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <div className="service-subcategory-screen">
        {/* Header with category image */}
        <header className="service-subcategory-header">
          {image && (
            <div className="service-subcategory-hero">
              <img
                src={`${url.serviceImageStartingPath}/${image}`}
                alt={category}
                className="service-subcategory-hero-image"
              />
              <div className="service-subcategory-hero-overlay">
                <h1 className="service-subcategory-title">
                  {category} Subcategories
                </h1>
              </div>
            </div>
          )}
        </header>

        {/* Search Bar */}
        <div className="service-subcategory-search-container">
          <input
            type="text"
            placeholder="Search subcategories..."
            className="service-subcategory-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {/* <button className="service-subcategory-search-button">
          <i className="fas fa-search"></i>
        </button> */}
        </div>

        {/* Content */}
        <main className="service-subcategory-main">
          {loading ? (
            <div className="service-subcategory-loading">
              <div className="service-subcategory-spinner"></div>
              <p>Loading subcategories...</p>
            </div>
          ) : error ? (
            <div className="service-subcategory-error">
              <i className="fas fa-exclamation-triangle"></i>
              <p>{error}</p>
              <button
                className="service-subcategory-retry-button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : filteredSubCategories.length === 0 ? (
            <div className="service-subcategory-empty">
              <i className="fas fa-folder-open"></i>
              <p>No subcategories available</p>
              {searchTerm && (
                <button
                  className="service-subcategory-clear-search"
                  onClick={() => setSearchTerm("")}
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <div className="service-subcategory-grid">
              {filteredSubCategories.map((subCategory) => (
                <div
                  key={subCategory.ID}
                  className="service-subcategory-card"
                  onClick={() => handleSubCategoryClick(subCategory)}
                >
                  <div className="service-subcategory-card-image-container">
                    <img
                      src={`${url.serviceImageStartingPath}/${subCategory.Image}`}
                      alt={subCategory.SubCategory}
                      className="service-subcategory-card-image"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/images/default-service.png";
                      }}
                    />
                  </div>
                  <div className="service-subcategory-card-content">
                    <h3 className="service-subcategory-card-title">
                      {subCategory.SubCategory}
                    </h3>
                    <p className="service-subcategory-card-description">
                      {subCategory.Description ||
                        "Explore services in this subcategory"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default ServiceSubCategoryScreen;
